/**
 * AnnualUpgradeBanner — nudge for active MONTHLY subscribers to switch
 * to yearly billing (2 months free). One-click upgrade via
 * POST /billing/upgrade-to-yearly (Stripe prorates the swap).
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CalendarClock, Loader2, X } from "lucide-react";

const DISMISS_KEY = "bfa_annual_upgrade_dismiss_ts";
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const MONTHLY_PRICE = { starter: 29, professional: 99, pro_radio: 299, network: 499 };

export default function AnnualUpgradeBanner({ compact = false }) {
  const [billing, setBilling] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try {
      const ts = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
      return Boolean(ts && Date.now() - ts < DISMISS_TTL_MS);
    } catch {
      return false;
    }
  });

  useEffect(() => {
    let alive = true;
    api.get("/billing/current").then((r) => alive && setBilling(r.data)).catch(() => {});
    return () => { alive = false; };
  }, []);

  // Funnel telemetry — record one "shown" per user per day (backend dedupes)
  useEffect(() => {
    if (!billing || (dismissed && !compact)) return;
    const monthly = MONTHLY_PRICE[billing.plan];
    if (!monthly || billing.cycle !== "monthly" || !billing.stripe_subscription_id) return;
    api.post("/billing/annual-nudge-event", { event: "shown" }).catch(() => {});
  }, [billing]); // eslint-disable-line react-hooks/exhaustive-deps

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* noop */ }
    setDismissed(true);
  };

  const upgrade = async () => {
    setBusy(true);
    api.post("/billing/annual-nudge-event", { event: "clicked" }).catch(() => {});
    try {
      const { data } = await api.post("/billing/upgrade-to-yearly");
      toast.success(`You're now on yearly billing — saving $${data.savings_usd}/yr.`);
      setDone(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upgrade failed — please try again");
    } finally {
      setBusy(false);
    }
  };

  if ((dismissed && !compact) || done || !billing) return null;
  const monthly = MONTHLY_PRICE[billing.plan];
  if (!monthly || billing.cycle !== "monthly" || !billing.stripe_subscription_id) return null;
  if (billing.status && !["active", "trialing"].includes(billing.status)) return null;
  const savings = monthly * 2; // yearly = 10× monthly → 2 months free

  if (compact) {
    return (
      <button
        type="button"
        onClick={upgrade}
        disabled={busy}
        data-testid="annual-upgrade-hint"
        className="mt-3 inline-flex items-center gap-1.5 text-xs text-emerald-300 hover:text-emerald-200 transition disabled:opacity-60"
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CalendarClock className="h-3.5 w-3.5" />}
        {busy ? "Switching to yearly…" : (
          <>You're on monthly billing — switch to yearly and save <span className="font-semibold">${savings}/yr</span> →</>
        )}
      </button>
    );
  }

  return (
    <div
      data-testid="annual-upgrade-banner"
      className="mb-6 rounded-xl border border-emerald-400/40 bg-gradient-to-r from-emerald-500/10 via-emerald-500/5 to-transparent p-5 flex flex-col md:flex-row items-start md:items-center justify-between gap-4"
    >
      <div className="flex items-start gap-3 flex-1 min-w-0">
        <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-emerald-500/15 text-emerald-300">
          <CalendarClock className="h-5 w-5" />
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-emerald-300 mb-1">
            // 2 MONTHS FREE
          </div>
          <div className="text-ink-primary text-base font-medium">
            Switch to yearly billing and save ${savings}/yr.{" "}
            <span className="text-ink-secondary font-normal">
              Same plan, same features — Stripe prorates the change instantly.
            </span>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          type="button"
          onClick={upgrade}
          disabled={busy}
          data-testid="annual-upgrade-btn"
          className="inline-flex items-center gap-2 rounded-md bg-emerald-400 px-5 py-2.5 text-sm font-semibold text-black hover:bg-emerald-300 transition disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {busy ? "Switching…" : `Switch to yearly — save $${savings}`}
        </button>
        <button
          type="button"
          onClick={dismiss}
          data-testid="annual-upgrade-dismiss"
          className="p-2 rounded-md text-ink-muted hover:text-ink-primary hover:bg-bg-elevated transition"
          title="Hide for 7 days"
          aria-label="Dismiss annual upgrade banner"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
